import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { ReactiveFormsModule, FormGroup, FormControl } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { RouterModule, Router } from '@angular/router';
import { MessageHandlerService } from '@core/services/message-handler/message-handler.service';
import { Store } from '@ngrx/store';
import { InputModule } from '@sharedModule/components/input/input.module';
import { sortDirection } from '@sharedModule/constants/data-grid.constants';
import { ATTRIBUTES_CONSTANT } from '@sharedModule/constants/fields.constant';
import { HasAccessDirective } from '@sharedModule/directives/has-access.directive';
import { MaterialModule } from '@sharedModule/material/material.module';
import { ConfirmationCommonDialogData } from '@sharedModule/models/confirmation-dialog.model';
import { ConfirmationDialogService } from '@sharedModule/services/confirmation-dialog/confirmation-dialog.service';
import { ExcelExportService } from '@sharedModule/services/excel-export/excel-export.service';
import { getActiveSort } from '@sharedModule/utils/sort.utils';
import { Subject, takeUntil } from 'rxjs';
import { VelocityLimitService } from '../../services/velocity-limit.service';
import {
  updateVelocityLimitListFilter,
  deleteVelocityLimit,
} from '../../store/velocity-limit-list.actions';
import { selectVelocityLimitList } from '../../store/velocity-limit-list.selector';
import { VelocityLimitListInterface } from '../../types/velocity-limit-list';
import {
  defaultVelocityLimitListFilter,
  VelocityLimitListFilter,
} from '../../types/velocity-limit-list-filter';

@Component({
  selector: 'app-velocity-limit-list',
  standalone: true,
  imports: [
    InputModule,
    CommonModule,
    RouterModule,
    MaterialModule,
    HasAccessDirective,
    ReactiveFormsModule,
  ],
  templateUrl: './velocity-limit-list.component.html',
  styleUrl: './velocity-limit-list.component.scss',
})
export class VelocityLimitListComponent implements OnInit, OnDestroy {
  public readonly attrConstant = ATTRIBUTES_CONSTANT.settings.velocityLimit;
  filterForm!: FormGroup;
  displayedColumns: string[] = [
    'id',
    'source_currency',
    'destination_currency',
    'source_country',
    'destination_country',
    'cash_pickup',
    'mobile_transfer',
    'bank_transfer',
    'wallet',
    'action',
  ];
  velocityLimitList?: VelocityLimitListInterface[];
  dataSource!: MatTableDataSource<VelocityLimitListInterface>;
  activeSort?: Sort;
  fallBackSort = {
    active: 'created_at',
    direction: sortDirection.desc,
  };
  page = 0;
  total = 0;
  limit = 10;
  subject = new Subject<{
    search: string;
  }>();
  #destroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private router: Router,
    private excelExportService: ExcelExportService,
    private velocityLimitService: VelocityLimitService,
    private messageHandlerService: MessageHandlerService,
    private confirmationDialogService: ConfirmationDialogService
  ) {}

  ngOnInit(): void {
    this.filterForm = new FormGroup({
      [this.attrConstant.search.key]: new FormControl(''),
    });
    this.getVelocityLimitList();
    this.dispatchFilter();
  }

  getVelocityLimitList(): void {
    this.store
      .select(selectVelocityLimitList)
      .pipe(takeUntil(this.#destroy$))
      .subscribe(data => {
        if (data) {
          this.velocityLimitList = data.rawData;
          this.dataSource = new MatTableDataSource(data.rawData);
          this.page = data.page;
          this.total = data.totalRecords;
          this.limit = data.count;
        }
      });
  }

  applyFilter(): void {
    this.subject.next(this.filterForm.value);
  }

  clearFilters(): void {
    this.filterForm.reset();
    this.dispatchFilter({ ...defaultVelocityLimitListFilter });
  }

  reload(): void {
    this.dispatchFilter();
  }

  onPageChange(pageEvent: PageEvent): void {
    this.dispatchFilter({
      count: pageEvent.pageSize,
      page: pageEvent.pageIndex,
    });
  }

  onSortChange(sortEvent: Sort): void {
    this.activeSort = getActiveSort(sortEvent, this.fallBackSort);
    this.dispatchFilter({
      sort_key: this.activeSort.active,
      sort_by: this.activeSort.direction,
    });
  }

  dispatchFilter(velocityLimitListFilter?: VelocityLimitListFilter): void {
    this.store.dispatch(
      updateVelocityLimitListFilter({ filter: velocityLimitListFilter ?? {} })
    );
  }

  onEdit(data: VelocityLimitListInterface): void {
    this.router.navigate(['/settings/velocity-limit/edit', data.id]);
  }

  export(): void {
    this.excelExportService.exportToExcel(
      this.velocityLimitList!,
      'velocity_limits'
    );
  }

  async onDeleteVelocity(data: VelocityLimitListInterface): Promise<void> {
    const alertCommonDialogData: ConfirmationCommonDialogData = {
      title: '',
      message: `Are you sure you're going to delete this velocity limit?`,
      submitButtonText: 'Confirm',
    };
    const isConfirmed =
      await this.confirmationDialogService.openConfirmationDialog(
        alertCommonDialogData
      );
    if (isConfirmed) {
      this.velocityLimitService
        .deleteVelocityLimit({ id: data.id })
        .pipe(takeUntil(this.#destroy$))
        .subscribe({
          next: x => {
            if (x.success) {
              this.messageHandlerService.show(x.message);
              this.store.dispatch(
                deleteVelocityLimit({
                  id: data.id,
                })
              );
            }
          },
        });
    }
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }
}
